import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  Wrench, Clock, CheckCircle2, Pause, AlertCircle,
  Factory, User, ChevronRight, X, FileSpreadsheet, Hash, TrendingUp,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = '/api';

const STATUS_META = {
  hotovo: { label: 'Hotovo', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2, dot: '#16A34A' },
  realizovano: { label: 'Realizováno', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Pause, dot: '#2563EB' },
  pending: { label: 'Čeká', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle, dot: '#F59E0B' },
};

function fmtCz(d) {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const p = s.split('-');
  if (p.length !== 3) return s;
  return `${p[2]}.${p[1]}.${p[0]}`;
}

function fmtDateTime(dt) {
  if (!dt) return '—';
  const s = String(dt);
  if (s.length < 16) return fmtCz(s);
  const datePart = fmtCz(s.slice(0, 10));
  const timePart = s.slice(11, 16);
  return `${datePart} ${timePart}`;
}

function fmtDuration(mins) {
  if (mins == null || mins === 0) return '—';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

// Generic sort helper
function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;  // nulls last
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'cs', { numeric: true, sensitivity: 'base' });
}

function useSortable(rows, defaultField = null, defaultDir = 'asc') {
  const [field, setField] = useState(defaultField);
  const [dir, setDir] = useState(defaultDir);
  const toggle = (f) => {
    if (field === f) {
      setDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setField(f);
      setDir('asc');
    }
  };
  const sorted = useMemo(() => {
    if (!field || !rows) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = typeof field === 'function' ? field(a) : a[field];
      const bv = typeof field === 'function' ? field(b) : b[field];
      const cmp = compareValues(av, bv);
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, field, dir]);
  return { sorted, field, dir, toggle };
}

function SortHeader({ label, field, current, direction, onClick, align = 'left', testid }) {
  const active = current === field;
  const Icon = active ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onClick(field)}
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] uppercase font-semibold transition-colors w-full ${
        align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'
      } ${active ? 'text-[#16A34A]' : 'text-[#94A3B8] hover:text-[#57534E]'}`}
    >
      <span>{label}</span>
      <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
    </button>
  );
}

export default function UdrzbaPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lastImport, setLastImport] = useState(null);

  // Detail modal
  const [detailMachine, setDetailMachine] = useState(null);
  const [detailWorker, setDetailWorker] = useState(null);
  const [detailRepairs, setDetailRepairs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, imports] = await Promise.all([
        axios.get(`${API}/maintenance/stats`, { withCredentials: true }),
        axios.get(`${API}/overviews/last-imports`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setLastImport(imports.data?.maintenance || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openMachineDetail = async (machine) => {
    setDetailMachine(machine);
    setDetailWorker(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/maintenance/repairs`, {
        params: { machine_code: machine.code, limit: 500 },
        withCredentials: true,
      });
      setDetailRepairs(data || []);
    } catch (e) {
      console.error(e);
      setDetailRepairs([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const openWorkerDetail = async (worker) => {
    setDetailWorker(worker);
    setDetailMachine(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/maintenance/repairs`, {
        params: { worker: worker.worker, limit: 500 },
        withCredentials: true,
      });
      setDetailRepairs(data || []);
    } catch (e) {
      console.error(e);
      setDetailRepairs([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailMachine(null);
    setDetailWorker(null);
    setDetailRepairs([]);
  };

  const filteredMachines = useMemo(() => {
    if (!stats?.machines) return [];
    const q = search.trim().toLowerCase();
    return stats.machines.filter(m =>
      !q || m.code?.toLowerCase().includes(q) || m.name?.toLowerCase().includes(q)
    );
  }, [stats, search]);

  if (loading) {
    return <Layout><div className="p-12 text-center text-[#57534E]">Načítám…</div></Layout>;
  }

  if (!stats || stats.total === 0) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="udrzba-empty">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-3">
              <Wrench className="w-8 h-8 text-[#16A34A]" /> Údržba
            </h1>
            <p className="text-[#57534E] mt-1">Přehled oprav strojů a pracovníků</p>
          </div>
          <Card className="bg-white border-[#E2E8F0] shadow-sm">
            <CardContent className="py-16 text-center">
              <FileSpreadsheet className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#052E16] font-['Manrope'] mb-2">Žádná data</h2>
              <p className="text-[#57534E]">Administrátor zatím nenahrál žádný export oprav. Importujte Excel v sekci <strong>Administrace → Údržba</strong>.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const sc = stats.status_counts || {};

  const filteredStatus = statusFilter === 'all' ? null : statusFilter;

  return (
    <Layout>
      <div className="space-y-6" data-testid="udrzba-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-3">
              <Wrench className="w-8 h-8 text-[#16A34A]" /> Údržba
            </h1>
            <p className="text-[#57534E] mt-1">Přehled oprav strojů a pracovníků ({stats.total} záznamů)</p>
            {lastImport?.date && (
              <p className="text-xs text-[#57534E] mt-1 flex items-center gap-1" data-testid="udrzba-data-validity" title={lastImport.filename || ''}>
                <Clock className="w-3 h-3 text-[#16A34A]" />
                Data platná k <strong className="text-[#1C1917]">{new Date(lastImport.date).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Status summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => setStatusFilter('all')}
            data-testid="status-card-all"
            className={`p-4 rounded-2xl border text-left transition-all ${statusFilter === 'all' ? 'bg-[#052E16] text-white border-[#052E16]' : 'bg-white border-[#E2E8F0] hover:border-[#16A34A]'}`}
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase opacity-70"><Hash className="w-3.5 h-3.5" /> Celkem</div>
            <div className="text-2xl font-bold mt-1">{stats.total}</div>
          </button>
          <button
            onClick={() => setStatusFilter('hotovo')}
            data-testid="status-card-hotovo"
            className={`p-4 rounded-2xl border text-left transition-all ${statusFilter === 'hotovo' ? 'bg-green-600 text-white border-green-600' : 'bg-white border-[#E2E8F0] hover:border-green-500'}`}
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase opacity-70"><CheckCircle2 className="w-3.5 h-3.5" /> Hotovo</div>
            <div className="text-2xl font-bold mt-1">{sc.hotovo || 0}</div>
          </button>
          <button
            onClick={() => setStatusFilter('realizovano')}
            data-testid="status-card-realizovano"
            className={`p-4 rounded-2xl border text-left transition-all ${statusFilter === 'realizovano' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-[#E2E8F0] hover:border-blue-500'}`}
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase opacity-70"><Pause className="w-3.5 h-3.5" /> Realizováno</div>
            <div className="text-2xl font-bold mt-1">{sc.realizovano || 0}</div>
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            data-testid="status-card-pending"
            className={`p-4 rounded-2xl border text-left transition-all ${statusFilter === 'pending' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-[#E2E8F0] hover:border-amber-500'}`}
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase opacity-70"><AlertCircle className="w-3.5 h-3.5" /> Čeká</div>
            <div className="text-2xl font-bold mt-1">{sc.pending || 0}</div>
          </button>
        </div>

        {/* Top 5 machines in last 30 days */}
        {stats.top_machines_30d && stats.top_machines_30d.length > 0 && (
          <Card className="bg-gradient-to-br from-[#FFF7ED] to-[#FEF3C7] border-amber-200 shadow-sm" data-testid="top-machines-30d-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold text-[#7C2D12] font-['Manrope'] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-700" />
                Top 5 problémových strojů – posledních 30 dní
                <span className="text-xs font-medium text-amber-700/70">od {fmtCz(stats.since_date)}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(() => {
                  const max = Math.max(...stats.top_machines_30d.map(m => m.total_repairs));
                  return stats.top_machines_30d.map((m, idx) => {
                    const pct = max > 0 ? (m.total_repairs / max) * 100 : 0;
                    const machine = stats.machines.find(x => x.code === m.code);
                    return (
                      <button
                        key={m.code}
                        onClick={() => machine && openMachineDetail(machine)}
                        data-testid={`top-machine-${m.code}`}
                        className="w-full text-left p-3 rounded-xl bg-white/70 hover:bg-white border border-amber-200 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 ${
                            idx === 0 ? 'bg-amber-500 text-white' :
                            idx === 1 ? 'bg-amber-400 text-white' :
                            idx === 2 ? 'bg-amber-300 text-amber-900' :
                            'bg-amber-100 text-amber-700'
                          }`}>{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="min-w-0">
                                <span className="font-mono text-xs text-amber-900/70 mr-2">{m.code}</span>
                                <span className="text-sm font-medium text-[#1C1917]">{m.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs shrink-0">
                                <span className="font-bold text-amber-900">{m.total_repairs}× oprav</span>
                                <span className="text-amber-700/70">{fmtDuration(m.total_minutes)}</span>
                              </div>
                            </div>
                            <div className="mt-1.5 h-1.5 bg-amber-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-amber-700/40 group-hover:text-amber-700 shrink-0" />
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Workers */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <User className="w-5 h-5 text-[#16A34A]" /> Pracovníci údržby
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left py-2 pr-3 font-semibold">Pracovník</th>
                    <th className="text-right py-2 px-3 font-semibold">Oprav</th>
                    <th className="text-right py-2 px-3 font-semibold">Hotovo</th>
                    <th className="text-right py-2 px-3 font-semibold">Probíhá</th>
                    <th className="text-right py-2 px-3 font-semibold">Celkový čas</th>
                    <th className="text-right py-2 pl-3 font-semibold">Průměr/oprava</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.workers.map(w => (
                    <tr
                      key={w.worker}
                      onClick={() => openWorkerDetail(w)}
                      data-testid={`worker-row-${w.worker}`}
                      className="border-b border-[#F1F5F9] hover:bg-[#F0FDF4] cursor-pointer transition-colors"
                    >
                      <td className="py-2.5 pr-3 font-medium text-[#1C1917]">{w.worker}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{w.total_repairs}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-green-700">{w.completed}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-blue-700">{w.in_progress}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-[#57534E]">{fmtDuration(w.total_minutes)}</td>
                      <td className="py-2.5 pl-3 text-right tabular-nums text-[#57534E]">{fmtDuration(w.avg_minutes)}</td>
                      <td className="py-2.5"><ChevronRight className="w-4 h-4 text-[#94A3B8]" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Machines */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Factory className="w-5 h-5 text-[#16A34A]" /> Stroje
              <span className="text-xs font-medium text-[#94A3B8]">({filteredMachines.length})</span>
            </CardTitle>
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Hledat stroj (kód nebo název)…"
              data-testid="machine-search"
              className="max-w-xs h-9 text-sm"
            />
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left py-2 pr-3 font-semibold">Kód</th>
                    <th className="text-left py-2 px-3 font-semibold">Název stroje</th>
                    <th className="text-right py-2 px-3 font-semibold">Počet oprav</th>
                    <th className="text-right py-2 px-3 font-semibold">Celkový čas</th>
                    <th className="text-right py-2 pl-3 font-semibold">Poslední</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMachines.map(m => (
                    <tr
                      key={m.code}
                      onClick={() => openMachineDetail(m)}
                      data-testid={`machine-row-${m.code}`}
                      className="border-b border-[#F1F5F9] hover:bg-[#F0FDF4] cursor-pointer transition-colors"
                    >
                      <td className="py-2.5 pr-3 font-mono text-xs text-[#52525B]">{m.code}</td>
                      <td className="py-2.5 px-3 text-[#1C1917]">{m.name}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{m.total_repairs}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-[#57534E]">{fmtDuration(m.total_minutes)}</td>
                      <td className="py-2.5 pl-3 text-right tabular-nums text-[#57534E]">{fmtCz(m.last_repair)}</td>
                      <td className="py-2.5"><ChevronRight className="w-4 h-4 text-[#94A3B8]" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredMachines.length === 0 && (
                <p className="py-6 text-center text-sm text-[#94A3B8]">Žádný stroj neodpovídá filtru.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!(detailMachine || detailWorker)} onOpenChange={(o) => { if (!o) closeDetail(); }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] text-[#052E16] flex items-center gap-2">
                {detailMachine ? (
                  <>
                    <Factory className="w-5 h-5 text-[#16A34A]" />
                    <span className="font-mono text-sm bg-[#F1F5F9] px-2 py-0.5 rounded">{detailMachine.code}</span>
                    <span>{detailMachine.name}</span>
                  </>
                ) : detailWorker ? (
                  <>
                    <User className="w-5 h-5 text-[#16A34A]" />
                    <span>{detailWorker.worker}</span>
                  </>
                ) : null}
              </DialogTitle>
            </DialogHeader>

            {/* Summary stripe */}
            {(detailMachine || detailWorker) && (
              <div className="grid grid-cols-3 gap-3 py-2">
                <div className="p-3 rounded-xl bg-[#F8FAF8] border border-[#E2E8F0]">
                  <div className="text-[10px] uppercase font-bold text-[#94A3B8]">Počet oprav</div>
                  <div className="text-xl font-bold text-[#052E16]">
                    {detailMachine ? detailMachine.total_repairs : detailWorker.total_repairs}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-[#F8FAF8] border border-[#E2E8F0]">
                  <div className="text-[10px] uppercase font-bold text-[#94A3B8]">Celkový čas</div>
                  <div className="text-xl font-bold text-[#052E16]">
                    {fmtDuration(detailMachine ? detailMachine.total_minutes : detailWorker.total_minutes)}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-[#F8FAF8] border border-[#E2E8F0]">
                  <div className="text-[10px] uppercase font-bold text-[#94A3B8]">
                    {detailMachine ? 'Poslední oprava' : 'Průměr na opravu'}
                  </div>
                  <div className="text-xl font-bold text-[#052E16]">
                    {detailMachine ? fmtCz(detailMachine.last_repair) : fmtDuration(detailWorker.avg_minutes)}
                  </div>
                </div>
              </div>
            )}

            {/* Repairs list */}
            {detailLoading ? (
              <p className="py-8 text-center text-[#94A3B8]">Načítám…</p>
            ) : (
              <div className="space-y-2">
                {detailRepairs
                  .filter(r => !filteredStatus || r.status === filteredStatus)
                  .map(r => {
                    const st = STATUS_META[r.status] || STATUS_META.pending;
                    const StIcon = st.icon;
                    return (
                      <div key={r.id} className="p-3 rounded-xl border border-[#E2E8F0] bg-white hover:bg-[#F8FAF8]" data-testid={`detail-repair-${r.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-xs text-[#52525B] bg-[#F1F5F9] px-1.5 py-0.5 rounded">{r.doklad}</span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.color}`}>
                                <StIcon className="w-3 h-3" /> {st.label}
                              </span>
                              {detailWorker && (
                                <span className="text-xs text-[#57534E]">
                                  <Factory className="w-3 h-3 inline mr-0.5" />
                                  <span className="font-mono">{r.machine_code}</span> · {r.machine_name}
                                </span>
                              )}
                              {detailMachine && r.worker && (
                                <span className="text-xs text-[#57534E]">
                                  <User className="w-3 h-3 inline mr-0.5" /> {r.worker}
                                </span>
                              )}
                            </div>
                            {r.description && <p className="text-sm text-[#1C1917] mt-1.5"><strong>Popis:</strong> {r.description}</p>}
                            {r.work_description && <p className="text-sm text-[#57534E] mt-1"><strong>Úkon:</strong> {r.work_description}</p>}
                          </div>
                          <div className="text-right text-xs text-[#57534E] shrink-0 min-w-[160px]">
                            <div><Clock className="w-3 h-3 inline mr-1" /> {fmtDateTime(r.start_datetime)}</div>
                            <div className="opacity-70">→ {fmtDateTime(r.end_datetime)}</div>
                            <div className="mt-1 font-semibold text-[#052E16]">{fmtDuration(r.duration_minutes)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {detailRepairs.length === 0 && (
                  <p className="py-6 text-center text-sm text-[#94A3B8]">Žádné opravy.</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
